import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Check,
  CheckCircle2,
  Loader2,
  MessageSquareWarning,
  Move3d,
  Music,
  Pause,
  Play,
  Plus,
  RefreshCw,
} from "lucide-react";
import { firstText } from "../episode-storyboard-board/utils";
import { getTimelineKeyboardTarget, isTimelinePlaybackShortcut } from "./timelineKeyboard";
import "./MiniStoryboardTimeline.css";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const DEFAULT_SEGMENT_TRACK_HEIGHT = 104;

const TimelineInsertButton = ({ insertIndex, disabled, onInsert, label, left = 0, width = 28, trackHeight = DEFAULT_SEGMENT_TRACK_HEIGHT }) => (
  <div
    className="mini-timeline__insert absolute top-0 flex items-center justify-center"
    style={{ left, width, height: trackHeight }}
    onPointerDown={(event) => event.stopPropagation()}
  >
    <div
      className="mini-timeline__insert-line h-full w-px"
      style={{ backgroundColor: "var(--color-border-secondary)" }}
    />
    <button
      type="button"
      disabled={disabled}
      title={label}
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation();
        onInsert(insertIndex);
      }}
      className="mini-timeline__insert-button absolute left-1/2 top-1/2 z-30 inline-flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full shadow-lg disabled:cursor-not-allowed"
      style={{
        backgroundColor: "var(--color-accent-primary)",
        color: "var(--color-accent-contrast, #fff)",
      }}
    >
      <Plus size={16} />
    </button>
  </div>
);

const TimelineRuler = ({ width, totalDuration, formatSeconds, getTimelinePixelAtSeconds }) => {
  const safeDuration = Math.max(0, Math.ceil(Number(totalDuration) || 0));
  const majorStep = safeDuration > 180 ? 30 : safeDuration > 80 ? 15 : 5;
  const minorStep = majorStep >= 30 ? 5 : 1;
  const ticks = [];
  for (let second = 0; second <= safeDuration; second += minorStep) {
    ticks.push({
      second,
      major: second % majorStep === 0,
    });
  }
  if (safeDuration && ticks[ticks.length - 1]?.second !== safeDuration) {
    ticks.push({ second: safeDuration, major: true });
  }

  return (
    <div
      className="mini-timeline__ruler relative h-7 text-[10px]"
      style={{
        width,
        color: "var(--color-text-muted)",
      }}
    >
      <div className="mini-timeline__ruler-line absolute left-0 right-0 top-2.5 h-px" style={{ backgroundColor: "var(--color-border-secondary)" }} />
      {ticks.map(({ second, major }) => {
        const left = getTimelinePixelAtSeconds(second);
        return (
          <div
            key={second}
            className="absolute top-0 flex -translate-x-1/2 flex-col items-center gap-0.5"
            style={{ left }}
          >
            <span
              className="mini-timeline__tick w-px"
              data-major={major}
              style={{
                height: major ? 12 : 6,
                backgroundColor: major ? "var(--color-text-muted)" : "var(--color-border-secondary)",
              }}
            />
            {major && <span className="mini-timeline__ruler-time font-mono">{formatSeconds(second)}</span>}
          </div>
        );
      })}
    </div>
  );
};

const TimelineIssuePin = ({
  issue,
  left,
  issuePriorityMeta,
  issueTypeName,
  formatSeconds,
  onSelect,
}) => {
  const meta = issuePriorityMeta(issue.priority);
  const resolved = ["resolved", "ignored"].includes(firstText(issue.status).toLowerCase());
  return (
    <button
      type="button"
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        onSelect(issue);
      }}
      className="group absolute top-1 z-50 flex -translate-x-1/2 flex-col items-center"
      style={{ left, opacity: resolved ? 0.52 : 1 }}
      title={`${meta.name} ${formatSeconds(issue.timecodeSeconds)} ${firstText(issue.title, "未命名问题")}`}
    >
      <span
        className="flex h-5 w-5 items-center justify-center rounded-full border shadow-lg"
        style={{ backgroundColor: meta.background, color: meta.color, borderColor: meta.color }}
      >
        {resolved ? <CheckCircle2 size={11} /> : <MessageSquareWarning size={11} />}
      </span>
      <span className="mt-0.5 h-3 w-px" style={{ backgroundColor: meta.color }} />
      <span
        className="pointer-events-none absolute left-1/2 top-full mt-2 hidden w-64 -translate-x-1/2 rounded-lg border p-3 text-left text-xs leading-5 shadow-2xl group-hover:block group-focus-visible:block"
        style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: meta.color, color: "var(--color-text-secondary)" }}
      >
        <span className="mb-1 block font-semibold" style={{ color: "var(--color-text-primary)" }}>
          {firstText(issue.title, "未命名问题")}
        </span>
        <span className="mb-1 block" style={{ color: meta.color }}>
          {meta.name} · {issueTypeName(issue.type)} · {formatSeconds(issue.timecodeSeconds)}
        </span>
        <span className="line-clamp-3 block">{firstText(issue.suggestion, issue.problem, "暂无修改说明")}</span>
      </span>
    </button>
  );
};

const MusicTrack = ({
  musicTrackBlocks,
  getTimelinePixelAtSeconds,
  formatSeconds,
  isMusicCueSuccess,
  timelineInsertWidth,
  timelineGap,
}) => (
  <div
    className="mini-timeline__track mini-timeline__track--music relative h-6 overflow-hidden"
    style={{
      marginLeft: timelineInsertWidth + timelineGap,
      borderTop: "1px solid var(--color-border-secondary)",
      borderBottom: "1px solid var(--color-border-secondary)",
    }}
  >
    {musicTrackBlocks.length ? musicTrackBlocks.map((cue) => {
      const left = Math.max(0, getTimelinePixelAtSeconds(cue.startSeconds) - timelineInsertWidth - timelineGap);
      const right = Math.max(left + 24, getTimelinePixelAtSeconds(cue.endSeconds) - timelineInsertWidth - timelineGap);
      const success = isMusicCueSuccess(cue);
      return (
        <div
          key={cue.id || cue.cueIndex}
          className="mini-timeline__music-clip absolute inset-y-0.5 flex min-w-0 items-center gap-1.5 rounded-md px-2 text-[11px]"
          data-ready={success}
          style={{
            left,
            width: Math.max(36, right - left),
            backgroundColor: success
              ? "color-mix(in srgb, var(--color-accent-primary) 12%, var(--color-bg-tertiary))"
              : "var(--color-bg-tertiary)",
            border: "1px solid var(--color-border-secondary)",
            borderColor: "var(--color-border-secondary)",
            color: "var(--color-text-muted)",
          }}
          title={`${firstText(cue.title, "音乐 cue")} ${formatSeconds(cue.startSeconds)}-${formatSeconds(cue.endSeconds)}`}
        >
          <Music size={12} className="shrink-0" />
          <span className="truncate">{firstText(cue.title, cue.emotion, "纯音乐")}</span>
          {!success && <span className="shrink-0">{firstText(cue.status, "待生成")}</span>}
        </div>
      );
    }) : (
      <div className="flex h-full items-center gap-2 px-3 text-[11px]" style={{ color: "var(--color-text-muted)" }}>
        <Music size={13} />
        <span>没有音乐轨。点击顶部生成音乐后，会按剧情情绪铺到底部时间线。</span>
      </div>
    )}
  </div>
);

const BlockingTrack = ({
  blockingTrackBlocks,
  getTimelinePixelAtSeconds,
  timelineInsertWidth,
  timelineGap,
  onOpenPlan,
}) => {
  if (!blockingTrackBlocks.length) return null;
  return (
    <div
      className="mini-timeline__track mini-timeline__track--blocking relative h-6 overflow-hidden"
      style={{
        marginLeft: timelineInsertWidth + timelineGap,
        borderTop: "1px solid var(--color-border-secondary)",
        borderBottom: "1px solid var(--color-border-secondary)",
      }}
    >
      {blockingTrackBlocks.map((block, index) => {
        const left = Math.max(0, getTimelinePixelAtSeconds(block.startSeconds) - timelineInsertWidth - timelineGap);
        const right = Math.max(left + 24, getTimelinePixelAtSeconds(block.endSeconds) - timelineInsertWidth - timelineGap);
        return (
          <button
            type="button"
            key={`${block.planId}-${block.startSeconds}-${index}`}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              onOpenPlan?.(block);
            }}
            className="mini-timeline__blocking-clip absolute inset-y-0.5 flex min-w-0 items-center gap-1.5 rounded-md px-2 text-[11px]"
            style={{
              left,
              width: Math.max(36, right - left),
              backgroundColor: "color-mix(in srgb, var(--color-accent-primary) 18%, var(--color-bg-tertiary))",
              border: "1px solid color-mix(in srgb, var(--color-accent-primary) 46%, var(--color-border-secondary))",
              borderColor: "color-mix(in srgb, var(--color-accent-primary) 46%, var(--color-border-secondary))",
              color: "var(--color-text-secondary)",
            }}
            title={[
              firstText(block.name, "站位方案"),
              block.mappings ? `绑定：${block.mappings}` : "",
              block.referenceUrl ? `参考图：${block.referenceUrl}` : "",
              "点击编辑",
            ].filter(Boolean).join("\n")}
          >
            <Move3d size={12} className="shrink-0" style={{ color: "var(--color-accent-primary)" }} />
            <span className="truncate">{firstText(block.name, "站位方案")}</span>
          </button>
        );
      })}
    </div>
  );
};

const TimelineSegmentPreview = ({ videoUrl, thumbnailUrl, title }) => {
  const previewRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      setVisible(true);
      observer.disconnect();
    });
    if (previewRef.current) observer.observe(previewRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={previewRef} className="mini-timeline__video-placeholder absolute inset-0" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
      {visible && (videoUrl ? (
        <video
          src={videoUrl}
          preload="metadata"
          muted
          playsInline
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-80"
          onLoadedMetadata={(event) => {
            // Request one decoded frame without playing or preloading every offscreen clip.
            const video = event.currentTarget;
            if (Number.isFinite(video.duration) && video.duration > 0) {
              video.currentTime = Math.min(0.1, video.duration / 2);
            }
          }}
        />
      ) : thumbnailUrl ? (
        <img src={thumbnailUrl} alt={title || "分镜组缩略图"} decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-80" />
      ) : null)}
    </div>
  );
};

const SegmentCard = ({
  segment,
  selected,
  duration,
  left,
  width,
  statusStyle,
  statusLabel,
  isVideoProcessing,
  isVideoFailed,
  formatSeconds,
  onClick,
  trackHeight = DEFAULT_SEGMENT_TRACK_HEIGHT,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="mini-timeline__segment relative shrink-0 cursor-pointer overflow-hidden rounded-lg border text-left"
    style={{
      position: "absolute",
      left,
      width,
      height: trackHeight,
      backgroundColor: "var(--color-bg-secondary)",
      borderColor: selected ? "var(--color-accent-primary)" : "var(--color-border-primary)",
      boxShadow: "none",
    }}
    aria-label={`定位到分镜组 ${segment.segmentIndex}`}
    aria-pressed={selected}
  >
    <TimelineSegmentPreview
      key={firstText(segment.videoUrl, segment.thumbnailUrl)}
      videoUrl={segment.videoUrl}
      thumbnailUrl={segment.thumbnailUrl}
      title={segment.title}
    />
    <div className="mini-timeline__segment-scrim absolute inset-0" aria-hidden="true" />
    <div className="absolute left-2 top-2 flex items-center gap-1.5">
      <span className="mini-timeline__segment-index rounded-md px-1.5 py-0.5 text-xs font-semibold" style={{ backgroundColor: "var(--overlay-medium)", color: "#fff" }}>
        {String(segment.segmentIndex).padStart(2, "0")}
      </span>
      <span className="rounded-full px-1.5 py-0.5 text-[10px]" style={statusStyle(segment)}>
        {isVideoProcessing(segment) && <Loader2 size={11} className="mr-1 inline animate-spin" />}
        {statusLabel(segment)}
      </span>
    </div>
    <div
      className="mini-timeline__segment-time absolute bottom-2 left-2 rounded px-1.5 py-0.5 font-mono text-[11px]"
      style={{
        backgroundColor: "var(--overlay-heavy)",
        border: "1px solid color-mix(in srgb, var(--color-accent-contrast) 14%, transparent)",
        color: "#fff",
        textShadow: "0 1px 2px rgba(0, 0, 0, 0.85)",
        backdropFilter: "blur(6px)",
      }}
    >
      {formatSeconds(segment.startSeconds)} · {formatSeconds(duration)}
    </div>
    <div className="mini-timeline__segment-title absolute bottom-7 left-2 right-8 truncate text-left text-[11px]" title={firstText(segment.title, "分镜组")} style={{ color: "var(--color-text-secondary)" }}>
      {firstText(segment.title, "分镜组")}
    </div>
    {isVideoFailed(segment) && (
      <div className="absolute inset-x-4 top-1/2 -translate-y-1/2 rounded px-2 py-1 text-center text-xs" style={statusStyle(segment)}>
        视频生成失败
      </div>
    )}
    {selected ? (
      <Check size={15} className="absolute bottom-3 right-3" style={{ color: "var(--color-accent-primary)" }} />
    ) : (
      <span className="mini-timeline__locate absolute bottom-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded-md" aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M5 2.5H2.5V5M11 2.5h2.5V5M13.5 11v2.5H11M5 13.5H2.5V11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      </span>
    )}
  </button>
);

const MiniStoryboardTimeline = ({
  workspaceMode = "full",
  mobileWorkspace = false,
  loading,
  timelineSegments,
  timelineLayout,
  selectedSegment,
  durationSeconds,
  totalDuration,
  playheadSeconds,
  playheadPixel,
  timelineScrollRef,
  timelineDragbarRef,
  timelineDragThumb,
  timelineScrollMetrics,
  updateTimelineScrollMetrics,
  handleTimelineDragbarPointerDown,
  revisionIssues,
  openRevisionIssueCount,
  openRevisionReview,
  musicCues,
  musicTrackBlocks,
  blockingTrackBlocks = [],
  openBlockingTrackPlan,
  musicEnabled,
  setMusicEnabled,
  playing,
  toggleTimelinePlayback,
  seekToTimeline,
  requestGenerateRemaining,
  getTimelinePixelAtSeconds,
  handleTimelineSegmentClick,
  insertManualSegment,
  statusStyle,
  statusLabel,
  isVideoProcessing,
  isVideoFailed,
  isMusicCueSuccess,
  formatSeconds,
  issuePriorityMeta,
  issueTypeName,
  primaryButtonStyle,
  ghostButtonStyle,
  subtleSurfaceStyle,
  timelineInsertWidth,
  timelineGap,
}) => {
  const hasSegments = timelineSegments.length > 0;
  const generatedCount = timelineSegments.filter((segment) => firstText(segment?.videoUrl)).length;
  const [showIssueTrack, setShowIssueTrack] = useState(true);
  const timelineContentRef = useRef(null);
  const playheadRef = useRef(null);
  const playheadColor = "var(--color-accent-primary)";
  const focusTimeline = workspaceMode === "focus";
  const compactTimeline = workspaceMode !== "full";
  const segmentTrackHeight = focusTimeline ? 74 : compactTimeline ? 88 : DEFAULT_SEGMENT_TRACK_HEIGHT;

  useEffect(() => {
    const scroll = timelineScrollRef?.current;
    if (!scroll || document.activeElement !== playheadRef.current) return;
    const margin = 32;
    const visibleLeft = scroll.scrollLeft + margin;
    const visibleRight = scroll.scrollLeft + scroll.clientWidth - margin;
    if (playheadPixel < visibleLeft) {
      scroll.scrollLeft = Math.max(0, playheadPixel - margin);
    } else if (playheadPixel > visibleRight) {
      scroll.scrollLeft = playheadPixel - scroll.clientWidth + margin;
    }
  }, [playheadPixel, timelineScrollRef]);

  const getTimelineSecondsAtPixel = useCallback((pixel) => {
    if (!timelineSegments.length) return 0;
    const boundedPixel = clamp(Number(pixel) || 0, 0, Math.max(0, timelineLayout.width || 0));
    for (const segment of timelineSegments) {
      const layout = timelineLayout.byIndex.get(Number(segment.segmentIndex));
      if (!layout) continue;
      if (boundedPixel < layout.left) return Number(segment.startSeconds) || 0;
      if (boundedPixel <= layout.right) {
        const duration = Math.max(1, Number(segment.durationSeconds) || durationSeconds);
        const ratio = clamp((boundedPixel - layout.left) / Math.max(1, layout.width), 0, 1);
        return (Number(segment.startSeconds) || 0) + ratio * duration;
      }
    }
    return totalDuration;
  }, [durationSeconds, timelineLayout, timelineSegments, totalDuration]);

  const seekByClientX = useCallback((clientX) => {
    const el = timelineContentRef.current;
    if (!el || !seekToTimeline) return;
    const rect = el.getBoundingClientRect();
    const seconds = getTimelineSecondsAtPixel(clientX - rect.left);
    seekToTimeline(seconds, { autoPlay: false });
  }, [getTimelineSecondsAtPixel, seekToTimeline]);

  const handlePlayheadPointerDown = useCallback((event) => {
    if (!hasSegments || !seekToTimeline) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.focus();
    seekByClientX(event.clientX);

    const handleMove = (moveEvent) => seekByClientX(moveEvent.clientX);
    const handleUp = () => {
      document.removeEventListener("pointermove", handleMove);
      document.removeEventListener("pointerup", handleUp);
    };
    document.addEventListener("pointermove", handleMove);
    document.addEventListener("pointerup", handleUp);
  }, [hasSegments, seekByClientX, seekToTimeline]);

  const handlePlayheadKeyDown = useCallback((event) => {
    if (!hasSegments || !seekToTimeline) return;
    if (isTimelinePlaybackShortcut(event)) {
      event.preventDefault();
      event.stopPropagation();
      if (!event.repeat) toggleTimelinePlayback?.();
      return;
    }
    const target = getTimelineKeyboardTarget(event.key, playheadSeconds, totalDuration, event.shiftKey);
    if (target == null) return;
    event.preventDefault();
    event.stopPropagation();
    seekToTimeline(target, { autoPlay: false });
  }, [hasSegments, playheadSeconds, seekToTimeline, toggleTimelinePlayback, totalDuration]);

  const handleTimelineSurfacePointerDown = useCallback((event) => {
    if (!hasSegments || !seekToTimeline) return;
    if (event.target.closest("button,[role='slider']")) return;
    event.preventDefault();
    seekByClientX(event.clientX);
    playheadRef.current?.focus({ preventScroll: true });
  }, [hasSegments, seekByClientX, seekToTimeline]);

  return (
    <div className={`mini-timeline ${focusTimeline ? "px-2 pb-1 pt-1" : "px-3 pb-2 pt-2"} shrink-0 overflow-visible`} style={{ backgroundColor: "var(--color-bg-overlay)", borderTop: "1px solid var(--color-border-primary)" }}>
      <div className={`mini-timeline__header ${focusTimeline ? "mb-1" : "mb-2"} grid ${mobileWorkspace ? "grid-cols-1" : "grid-cols-[minmax(0,1fr)_auto]"} items-start gap-2`}>
        <div className="min-w-0">
          <div className={`flex min-w-0 items-center gap-2 ${mobileWorkspace ? "flex-wrap" : ""}`}>
            <span className="mini-timeline__title text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>粗剪时间轴</span>
            <span className="rounded-full px-2 py-0.5 text-xs" style={statusStyle(selectedSegment)}>
              当前 {String(selectedSegment?.segmentIndex || 1).padStart(2, "0")}
            </span>
            <span className="mini-timeline__timecode font-mono text-xs" style={{ color: "var(--color-text-muted)" }}>
              {formatSeconds(playheadSeconds)} / {formatSeconds(totalDuration)}
            </span>
          </div>
          <div className={`${focusTimeline ? "hidden" : "mt-0.5"} text-[11px]`} style={{ color: "var(--color-text-muted)" }}>
            {hasSegments ? `${timelineSegments.length} 个片段 · ${generatedCount} 个已生成 · 点击刻度/轨道跳转，选中指针后按空格播放/暂停` : "当前集还没有小故事板片段"}
          </div>
        </div>
        <div className={`flex flex-wrap items-center gap-2 ${mobileWorkspace ? "justify-start" : "justify-end"}`}>
          <button
            type="button"
            onClick={() => openRevisionReview?.()}
            className="inline-flex h-8 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3 text-xs"
            style={ghostButtonStyle}
            title="打开独立审片与问题反馈页面"
          >
            <MessageSquareWarning size={16} />
            {!focusTimeline && <span>问题反馈</span>}
            {openRevisionIssueCount > 0 && (
              <span className="rounded-full px-1.5 py-0.5 text-[10px]" style={{ backgroundColor: "var(--color-bg-overlay)", color: "var(--color-accent-primary)" }}>
                {openRevisionIssueCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setShowIssueTrack((value) => !value)}
            className="inline-flex h-8 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3 text-xs"
            style={showIssueTrack ? ghostButtonStyle : subtleSurfaceStyle}
            title={showIssueTrack ? "隐藏时间轴上的问题反馈轨" : "显示时间轴上的问题反馈轨"}
          >
            <span>{focusTimeline ? (showIssueTrack ? "隐藏" : "显示") : (showIssueTrack ? "隐藏反馈" : "显示反馈")}</span>
          </button>
          {musicCues.length > 0 && (
            <button
              type="button"
              onClick={() => setMusicEnabled((value) => !value)}
              className="inline-flex h-8 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3 text-xs disabled:opacity-40"
              style={musicEnabled ? ghostButtonStyle : subtleSurfaceStyle}
            >
              <Music size={16} />
              {!focusTimeline && <span>{musicEnabled ? "音乐开" : "音乐关"}</span>}
            </button>
          )}
          <button
            type="button"
            onClick={toggleTimelinePlayback}
            disabled={!hasSegments}
            className="inline-flex h-8 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3 text-xs disabled:opacity-40"
            style={primaryButtonStyle}
            aria-label={playing ? "暂停粗剪播放" : "从当前指针播放"}
            title="选中时间指针后可按空格播放或暂停"
          >
            {playing ? <Pause size={17} /> : <Play size={17} />}
            {!focusTimeline && <span>{playing ? "暂停" : "播放"}</span>}
          </button>
        </div>
      </div>

      <div
        ref={timelineScrollRef}
        onScroll={updateTimelineScrollMetrics}
            className="overflow-x-scroll overflow-y-visible pb-1 mini-storyboard-timeline-scrollbar"
      >
        {!hasSegments ? (
          <button
            type="button"
            onClick={requestGenerateRemaining}
            disabled={Boolean(loading)}
            className="flex h-20 min-w-[240px] items-center justify-center rounded-xl border border-dashed text-sm disabled:opacity-50"
            style={{ ...subtleSurfaceStyle, color: "var(--color-text-muted)" }}
          >
            <RefreshCw size={16} className="mr-2" />
            生成粗剪分镜
          </button>
        ) : (
          <div
            ref={timelineContentRef}
            className="mini-timeline__tracks relative space-y-1"
            onPointerDown={handleTimelineSurfacePointerDown}
            style={{
              width: `${timelineLayout.width}px`,
              minWidth: "100%",
            }}
          >
            <TimelineRuler
              width={timelineLayout.width}
              totalDuration={totalDuration}
              formatSeconds={formatSeconds}
              getTimelinePixelAtSeconds={getTimelinePixelAtSeconds}
            />
            <div
              ref={playheadRef}
              role="slider"
              tabIndex={0}
              aria-label="粗剪时间指针，空格播放或暂停，方向键调整一秒，Shift 加方向键调整五秒"
              aria-keyshortcuts="Space ArrowLeft ArrowRight ArrowUp ArrowDown PageUp PageDown Home End"
              aria-valuemin={0}
              aria-valuemax={Math.round(totalDuration)}
              aria-valuenow={Math.round(playheadSeconds)}
              aria-valuetext={formatSeconds(playheadSeconds)}
              onPointerDown={handlePlayheadPointerDown}
              onKeyDown={handlePlayheadKeyDown}
              className="mini-timeline__playhead absolute z-40 w-5 -translate-x-1/2 cursor-ew-resize touch-none"
              style={{
                left: `${playheadPixel}px`,
                top: 34,
                bottom: 2,
              }}
            >
              <span
                className="mini-timeline__playhead-line absolute bottom-0 left-1/2 top-0 w-px -translate-x-1/2"
                style={{
                  backgroundColor: playheadColor,
                  boxShadow: `0 0 14px ${playheadColor}`,
                }}
              />
              <span
                className="mini-timeline__playhead-cap absolute left-1/2 top-0 h-3 w-3 -translate-x-1/2 rounded-full"
                style={{
                  backgroundColor: playheadColor,
                  boxShadow: `0 0 12px ${playheadColor}`,
                }}
              />
            </div>

            {showIssueTrack && (
              <div
                className="mini-timeline__track mini-timeline__track--issues relative h-6 overflow-visible"
                style={{
                  marginLeft: timelineInsertWidth + timelineGap,
                  borderTop: "1px solid var(--color-border-secondary)",
                }}
              >
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[11px]" style={{ color: "var(--color-text-muted)" }}>
                  反馈
                </div>
                {revisionIssues.map((issue) => (
                  <TimelineIssuePin
                    key={issue.id}
                    issue={issue}
                    left={getTimelinePixelAtSeconds(Number(issue.timecodeSeconds) || 0) - timelineInsertWidth - timelineGap}
                    issuePriorityMeta={issuePriorityMeta}
                    issueTypeName={issueTypeName}
                    formatSeconds={formatSeconds}
                    onSelect={(selectedIssue) => openRevisionReview?.(selectedIssue)}
                  />
                ))}
              </div>
            )}

            <BlockingTrack
              blockingTrackBlocks={blockingTrackBlocks}
              getTimelinePixelAtSeconds={getTimelinePixelAtSeconds}
              timelineInsertWidth={timelineInsertWidth}
              timelineGap={timelineGap}
              onOpenPlan={openBlockingTrackPlan}
            />

            <MusicTrack
              musicTrackBlocks={musicTrackBlocks}
              getTimelinePixelAtSeconds={getTimelinePixelAtSeconds}
              formatSeconds={formatSeconds}
              isMusicCueSuccess={isMusicCueSuccess}
              timelineInsertWidth={timelineInsertWidth}
              timelineGap={timelineGap}
            />

            <div className="mini-timeline__track mini-timeline__track--video relative" style={{ width: timelineLayout.width, height: segmentTrackHeight }}>
              <TimelineInsertButton
                insertIndex={1}
                disabled={Boolean(loading)}
                onInsert={insertManualSegment}
                label="在轨道开头插入片段"
                left={0}
                width={timelineInsertWidth}
                trackHeight={segmentTrackHeight}
              />
              {timelineSegments.map((segment) => {
                const selected = Number(segment.segmentIndex) === Number(selectedSegment?.segmentIndex);
                const duration = Number(segment.durationSeconds) || durationSeconds;
                const layout = timelineLayout.byIndex.get(Number(segment.segmentIndex));
                const segmentLeft = layout?.left || 0;
                const segmentWidth = layout?.width || 180;
                return (
                  <React.Fragment key={segment.id || segment.segmentIndex}>
                    <SegmentCard
                      segment={segment}
                      selected={selected}
                      duration={duration}
                      left={segmentLeft}
                      width={segmentWidth}
                      statusStyle={statusStyle}
                      statusLabel={statusLabel}
                      isVideoProcessing={isVideoProcessing}
                      isVideoFailed={isVideoFailed}
                      formatSeconds={formatSeconds}
                      onClick={(event) => {
                        handleTimelineSegmentClick(segment, event);
                        if (event.detail > 0) playheadRef.current?.focus({ preventScroll: true });
                      }}
                      trackHeight={segmentTrackHeight}
                    />
                    <TimelineInsertButton
                      insertIndex={Number(segment.segmentIndex) + 1}
                      disabled={Boolean(loading)}
                      onInsert={insertManualSegment}
                      label={`在分镜组 ${String(segment.segmentIndex).padStart(2, "0")} 后插入片段`}
                      left={(layout?.right || segmentLeft + segmentWidth) + timelineGap}
                      width={timelineInsertWidth}
                      trackHeight={segmentTrackHeight}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {timelineDragThumb.visible && (
        <div
          ref={timelineDragbarRef}
          role="scrollbar"
          aria-label="粗剪台横向拖动条"
          aria-orientation="horizontal"
          aria-valuemin={0}
          aria-valuemax={Math.max(0, timelineScrollMetrics.scrollWidth - timelineScrollMetrics.clientWidth)}
          aria-valuenow={Math.round(timelineScrollMetrics.scrollLeft)}
          onPointerDown={handleTimelineDragbarPointerDown}
          className="mt-1 h-2 w-full cursor-grab rounded-full active:cursor-grabbing mini-storyboard-timeline-dragbar"
        >
          <div
            className="h-full rounded-full mini-storyboard-timeline-dragbar-thumb"
            style={{
              width: `${timelineDragThumb.width}px`,
              transform: `translateX(${timelineDragThumb.left}px)`,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default MiniStoryboardTimeline;
